import Resource from './resource'
import Game from './game'
import OrderItem from './order-item'

export default class Order extends Resource {
  constructor ({ attrs = {}, gamesRoot, ...params } = {}) {
    super({ attrs, ...params })
    this.gamesRoot = gamesRoot

    this.data.games
      = attrs.games
        ? attrs.games.map((game) => {
            return new Game({ attrs: game, root: this.gamesRoot, parent: this })
          })
        : []

    this.data.items
      = attrs.items
        ? attrs.items.map((item) => {
            return new OrderItem({ attrs: item, parent: this })
          })
        : []
  }

  get activeGames () {
    return this.data.games?.filter(g => !g.removedAt && !g.canceledAt) || []
  }

  get activeItems () {
    return this.data.items?.filter(i => !i.removedAt && !i.canceledAt) || []
  }

  // Removed only, canceled ones count: the same as Order#nothing_to_place?
  get nothingToPlace () {
    return !this.data.games?.some(g => !g.removedAt) && !this.data.items?.some(i => !i.removedAt)
  }

  get prebookingCodeOnlyGames () {
    return this.activeGames?.filter(g => g?.room?.prebookingCodeOnly) || []
  }

  get asJson () {
    const json = super.asJson
    json.gamesAttributes = json.games.map(g => g.asJson)
    delete json.games
    json.itemsAttributes = json.items.map(i => i.asJson)
    delete json.items
    return json
  }

  buildGame (attrs = {}) {
    const game = new Game({ attrs, root: this.gamesRoot })
    this.data.games.push(game)
    return game
  }

  async cancelGame (index) {
    this.data.games[index] = await this.data.games[index].cancel()
    return this.data.games[index]
  }

  async deleteGame (index) {
    let game = this.data.games[index]

    if (game.isPersisted) {
      this.data.games[index] = await game.delete()
    } else {
      game = this.data.games.splice(index, 1)[0]
    }

    return game
  }

  buildItem ({ attrs }) {
    const item = new OrderItem({ attrs, parent: this })
    this.data.items.push(item)
    return item
  }

  async cancelItem (index) {
    this.data.items[index] = await this.data.items[index].cancel()
    return this.data.items[index]
  }

  async deleteItem (index) {
    let item = this.data.items[index]

    if (item.isPersisted) {
      this.data.items[index] = await item.delete()
    } else {
      item = this.data.items.splice(index, 1)[0]
    }

    return item
  }

  async retrieve () {
    return super.retrieve({ gamesRoot: this.gamesRoot })
  }

  asConversion () {
    return {
      transaction_id: this.confirmationCode,
      value: this.endCost,
      tax: this.applicationFee,
      shipping: this.processingFee,
      currency: 'USD',
      coupon: this.giftCardString || this.promoCodeString,
      items: this.games.map(game => game.asConversion()),
    }
  }

  set gamesRoot (root) {
    this._gamesRoot = root
  }

  get gamesRoot () {
    return this._gamesRoot
  }

  set itemsRoot (root) {
    this._itemsRoot = root
  }

  get itemsRoot () {
    return this._itemsRoot
  }

  get hasGamesItemsMixedError () {
    return this.details?.base?.findIndex(e => e.error === 'games_items_mixed') >= 0
  }

  get hasMixedLocationsError () {
    return this.details?.games?.findIndex(e => e.error === 'different_locations') >= 0
  }
}

Order.paramName = 'order'
